import { useTranslation } from "react-i18next";
import { ORDER_STATUS_CANCELED, ORDER_STATUS_CANCELLED } from "../../../../../constants/OrderConstant";
import { CHEFS_FARM, GROWY } from "../../../../../constants/CompanyConstants";
import _ from "lodash";
import React, { useMemo, useState } from "react";
import { Button, Card, Select, Space, Table } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { productsReport } from "../../../../../urls";
import { CURRENCY } from "../../../../../constants/DeliveryConstants";
import { formatPrice, formatWeight } from "../../../../../functions/formatters";
import { useProducts } from "../../../../../data/hooks/firestore/useProducts";
import { useCustomers } from "../../../../../data/hooks/firestore/useCustomers";
import { useStockReport } from "../../../../../data/hooks/firestore/useStockReport";
import utils from "../../../../../utils";
import { expandCartBundles } from "../../../../../functions/reports";
import { StyledSelect } from "../../../../../styles/sharedStyles";

const TopSellingProducts = ({ orders, dateRangeText, companyFilter, marketingCodeFilter = "All", dateRange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { products, getProductById } = useProducts();
  const { customers } = useCustomers();
  const { getAggregatedAddedStock } = useStockReport(dateRange);
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Get unique categories from products
  const categories = useMemo(() => {
    if (!products) return [];
    const uniqueCategories = [...new Set(products.map((p) => p.productCategory).filter(Boolean))];
    return uniqueCategories.sort();
  }, [products]);

  // Get packed products data
  const packedProductsData = useMemo(() => {
    const aggregatedStock = getAggregatedAddedStock(companyFilter, products);
    const packedMap = {};
    aggregatedStock.forEach((item) => {
      packedMap[item.productId] = item.totalAddedStock;
    });
    return packedMap;
  }, [getAggregatedAddedStock, companyFilter, products]);

  // Process orders to get product quantities and weights
  const getTopSellingProducts = () => {
    // Apply company filter first
    let filteredOrders = orders.filter(
      (order) => order.farmorderStatus !== ORDER_STATUS_CANCELED && order.farmorderStatus !== ORDER_STATUS_CANCELLED,
    );

    // Apply company filter based on customer.company
    if (companyFilter !== "All") {
      filteredOrders = filteredOrders.filter((order) => {
        const customer = customers.find((c) => c.id === order.farmorderCustomerId);
        const customerCompany = customer?.company;

        if (companyFilter === GROWY) {
          return customerCompany === GROWY;
        } else if (companyFilter === CHEFS_FARM) {
          return customerCompany === CHEFS_FARM || !customerCompany;
        }
        return false;
      });
    }

    // Apply marketing code filter based on customer.marketingCode
    if (marketingCodeFilter !== "All") {
      filteredOrders = filteredOrders.filter((order) => {
        const customer = customers.find((c) => c.id === order.farmorderCustomerId);
        return customer?.marketingCode === marketingCodeFilter;
      });
    }

    const productQuantities = {};

    filteredOrders.forEach((order) => {
      if (order.userShoppingCart && Array.isArray(order.userShoppingCart)) {
        // Expand bundles
        const expandedCart = expandCartBundles(order.userShoppingCart, products || []);

        expandedCart.forEach((product) => {
          const productId = product.productId;
          const productDetails = getProductById(productId);
          const productWeight = productDetails?.weight || 0;
          const productCategory = productDetails?.productCategory || product.productCategory || "";

          if (productQuantities[productId]) {
            productQuantities[productId].totalQuantity += product.itemCount;
            productQuantities[productId].totalValue += (product.productPrice || 0) * product.itemCount;
            productQuantities[productId].totalWeight += product.itemCount * productWeight;
          } else {
            productQuantities[productId] = {
              id: productId,
              name: product.productName,
              category: productCategory,
              img: product.productImageDownloadURL || "",
              totalQuantity: product.itemCount,
              totalValue: (product.productPrice || 0) * product.itemCount,
              totalWeight: product.itemCount * productWeight,
              weight: productWeight,
            };
          }
        });
      }
    });

    // Add packed quantities to all products
    Object.keys(productQuantities).forEach((productId) => {
      productQuantities[productId].totalPacked = packedProductsData[productId] || 0;
    });

    // Convert to array and apply category filter
    let productsArray = Object.values(productQuantities);

    if (selectedCategory !== "all") {
      productsArray = productsArray.filter((p) => p.category === selectedCategory);
    }

    return _.orderBy(productsArray, ["totalQuantity"], ["desc"]).slice(0, 5);
  };

  const topProducts = getTopSellingProducts();

  const handleSeeFullReport = () => {
    const currentParams = searchParams.toString();
    navigate(`${productsReport}${currentParams ? `?${currentParams}` : ""}`);
  };

  const columns = [
    {
      title: t("product_name"),
      dataIndex: "name",
      sorter: (a, b) => utils.antdTableSorter(a, b, "name"),
    },
    {
      title: t("quantity"),
      dataIndex: "totalQuantity",
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalQuantity"),
    },
    {
      title: t("packed_units"),
      dataIndex: "totalPacked",
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalPacked"),
    },
    {
      title: t("weight"),
      dataIndex: "totalWeight",
      render: (weight) => formatWeight(weight),
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalWeight"),
    },
    {
      title: t("revenue"),
      dataIndex: "totalValue",
      render: (value) => <span className="font-weight-semibold">{CURRENCY + formatPrice(value || 0)}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalValue"),
    },
    {
      title: t("avg_price"),
      dataIndex: "averagePricePerKg",
      render: (_, record) => {
        if (record.weight > 0 && record.totalWeight > 0) {
          const avgPrice = (record.totalValue / record.totalWeight) * 1000;
          return CURRENCY + formatPrice(avgPrice) + "/kg";
        }
        return "-";
      },
      sorter: (a, b) => {
        const avgA = a.weight > 0 && a.totalWeight > 0 ? a.totalValue / a.totalWeight : 0;
        const avgB = b.weight > 0 && b.totalWeight > 0 ? b.totalValue / b.totalWeight : 0;
        return avgA - avgB;
      },
    },
  ];

  return (
    <Card
      title={t("top_selling_products")}
      extra={
        <Button type="link" onClick={handleSeeFullReport}>
          {t("see_full_report")}
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
        <span className="text-muted">{dateRangeText}</span>
        <StyledSelect
          style={{ width: 200 }}
          $isFiltered={selectedCategory !== "all"}
          value={selectedCategory}
          onChange={setSelectedCategory}
        >
          <Select.Option value="all">{t("all_categories")}</Select.Option>
          {categories.map((category) => (
            <Select.Option key={category} value={category}>
              {category}
            </Select.Option>
          ))}
        </StyledSelect>
      </Space>
      <Table
        showSorterTooltip={false}
        columns={columns}
        dataSource={topProducts}
        rowKey="id"
        pagination={false}
        scroll={{ x: 700 }}
      />
    </Card>
  );
};

export default TopSellingProducts;
